import React from 'react';
import { Link } from 'react-router-dom';

interface DashboardHeroProps {
  /** Greeting name — display name, then email prefix, then a fallback. */
  greetingName: string;
  /** Romanian-localized full date string (e.g. "luni, 15 iunie 2026"). */
  todayLabel: string;
  /** Time-of-day greeting prefix (`Bună dimineața` / `Bună ziua` / `Bună seara`). */
  greeting: string;
  /** When `true`, render the "Vizualizare avansată" chip; else "simplificată". */
  isAdvanced: boolean;
  /** Whether the current user has any families OR shared members. Drives
   *  the primary CTA's destination — when zero, sends them straight to
   *  the create-family flow. */
  hasAnyFamily: boolean;
  /** Whether the user has any inbound shared members — controls the
   *  optional "Membri partajați" CTA. */
  hasSharedMembers: boolean;
}

const IconCalendar = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const IconShield = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

const IconUsers = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const IconChat = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const IconShare = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="18" cy="5" r="3" />
    <circle cx="6" cy="12" r="3" />
    <circle cx="18" cy="19" r="3" />
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
  </svg>
);

const IconCog = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h0a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

/**
 * Open the in-app chat bubble. Implemented as a window-level custom
 * event so the dashboard doesn't need to lift the bubble's open state
 * up the tree — `MedicalChatBubble` listens for `edosar:open-chat`
 * and opens itself.
 */
function dispatchOpenChat(): void {
  window.dispatchEvent(new CustomEvent('edosar:open-chat'));
}

/**
 * Open the profile modal (view-mode tab). Same dispatch pattern as
 * `dispatchOpenChat` — `Navbar.tsx` listens for `edosar:open-profile`.
 */
function dispatchOpenProfile(): void {
  window.dispatchEvent(new CustomEvent('edosar:open-profile'));
}

const DashboardHero: React.FC<DashboardHeroProps> = ({
  greetingName,
  todayLabel,
  greeting,
  isAdvanced,
  hasAnyFamily,
  hasSharedMembers,
}) => {
  const primaryTo = hasAnyFamily ? '/families' : '/families?create=1';
  const primaryLabel = hasAnyFamily ? 'Familiile mele' : 'Creează prima familie';

  return (
    <header className="dash-hero" aria-labelledby="dashboard-heading">
      <div className="dash-hero-inner">
        <p className="dash-hero-kicker">Panou principal</p>
        <h1 id="dashboard-heading" className="dash-hero-title">
          {greeting}, <span className="dash-hero-name">{greetingName}</span>
        </h1>
        <p className="dash-hero-sub">
          Toate dosarele medicale ale familiei tale, într-un singur loc — organizate, criptate
          și mereu la îndemână.
        </p>

        <div className="dash-hero-ctas">
          <Link to={primaryTo} className="btn btn-primary">
            <IconUsers />
            {primaryLabel}
          </Link>
          <button
            type="button"
            className="btn dash-hero-cta-secondary"
            onClick={dispatchOpenChat}
          >
            <IconChat />
            Deschide chat
          </button>
          {hasSharedMembers && (
            <Link to="/shared-members" className="btn dash-hero-cta-secondary">
              <IconShare />
              Membri partajați
            </Link>
          )}
        </div>

        <div className="dash-hero-meta">
          <span className="dash-hero-pill">
            <IconCalendar />
            {todayLabel}
          </span>
          <span className="dash-hero-pill">
            <IconShield />
            Date private, criptate end-to-end
          </span>
          <button
            type="button"
            className="dash-hero-pill dash-hero-pill-action"
            onClick={dispatchOpenProfile}
            title="Schimbă din profilul tău"
            aria-label={
              isAdvanced
                ? 'Vizualizare avansată — schimbă din profil'
                : 'Vizualizare simplificată — schimbă din profil'
            }
          >
            <IconCog />
            {isAdvanced ? 'Vizualizare avansată' : 'Vizualizare simplificată'}
          </button>
        </div>
      </div>
    </header>
  );
};

export default DashboardHero;
